import React, { useEffect } from 'react';
import { BackButton, Input, Checkbox, ErrorAlert, ErrorMessage } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import usePricerApi from '../Hooks/usePricerApi';
import { ApiError, EntryTable, EntryTableColumn, ProviderMapper } from '../Components/Combined';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { ESRD as modelType } from '../Constants/modelTypes';
import useCalendarYear from '../Hooks/useCalendarYear';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, formatStringAsCurrency, getDefaultDateString } from '../Utils/stringUtils';
import Button from '../Components/Simple/Button';
import { validateBilled, validateLineServiceDate } from '../Utils/validation';
import { mapToSetters } from '../Utils/mapping';
import prepareServiceLines from '../Utils/prepareServiceLines';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { transformNumberToOneDecimalPlace, transformStringToUpCase } from '../Utils/valueTransforms';
import { enableAsyncPricingApi, enableESRDNdc } from '../Utils/features';

export default function ESRD() {
    const location = useLocation();
    const { prePopulatedMedicareId, prePopulatedNPI } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [providerName, setProviderName] = useSessionState('', 'providerName');
    const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
    const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
    const [providerZipCode, setProviderZipCode] = useSessionState('', 'zipCode');
    const [fromDate, setFromDate] = useSessionState(getDefaultDateString(), 'fromDate');
    const [toDate, setToDate] = useSessionState(getDefaultDateString(), 'toDate');
    const [calendarYear, setCalendarYearInputDate] = useCalendarYear(null);
    const [height, setHeight] = useSessionState('', 'height');
    const [weight, setWeight] = useSessionState('', 'width');
    const [ndc, setNdc] = useSessionState('', 'ndc');
    const [dateOfBirth, setDateOfBirth] = useSessionState('', 'dateOfBirth');
    const [dialysisOnsetDate, setDialysisOnsetDate] = useSessionState('', 'dialysisOnsetDate');
    const [pericarditis, setPericarditis] = useSessionState(false, 'pericarditis');
    const [gastrolTractBleeding, setGastroTractBleeding] = useSessionState(false, 'gastrolTractBleeding');
    const [hereditaryAnemia, setHereditaryAnemia] = useSessionState(false, 'hereditaryAnemia');
    const [myelodysplasticSyndrome, setMyelodysplasticSyndrome] = useSessionState(false, 'myelodysplasticSyndrome');
    const [dialysisConditionCodeOne, setDialysisConditionCodeOne] = useSessionState('', 'dialysisConditionCodeOne');
    const [dialysisConditionCodeTwo, setDialysisConditionCodeTwo] = useSessionState('', 'dialysisConditionCodeTwo');
    const [dialysisConditionCodeThree, setDialysisConditionCodeThree] = useSessionState('', 'dialysisConditionCodeThree');
    const [dialysisConditionCodeFour, setDialysisConditionCodeFour] = useSessionState('', 'dialysisConditionCodeFour');
    const [dialysisConditionCodeFive, setDialysisConditionCodeFive] = useSessionState('', 'dialysisConditionCodeFive');
    const [diagnosisCodeOne, setDiagnosisCodeOne] = useSessionState('', 'diagnosisCodeOne');
    const [diagnosisCodeTwo, setDiagnosisCodeTwo] = useSessionState('', 'diagnosisCodeTwo');
    const [diagnosisCodeThree, setDiagnosisCodeThree] = useSessionState('', 'diagnosisCodeThree');
    const [diagnosisCodeFour, setDiagnosisCodeFour] = useSessionState('', 'diagnosisCodeFour');
    const [diagnosisCodeFive, setDiagnosisCodeFive] = useSessionState('', 'diagnosisCodeFive');
    const [diagnosisCodeSix, setDiagnosisCodeSix] = useSessionState('', 'diagnosisCodeSix');
    const [diagnosisCodeSeven, setDiagnosisCodeSeven] = useSessionState('', 'diagnosisCodeSeven');
    const [diagnosisCodeEight, setDiagnosisCodeEight] = useSessionState('', 'diagnosisCodeEight');
    const [diagnosisCodeNine, setDiagnosisCodeNine] = useSessionState('', 'diagnosisCodeNine');
    const [diagnosisCodeTen, setDiagnosisCodeTen] = useSessionState('', 'diagnosisCodeTen');

    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);

    const navigate = useNavigate();

    const getNewLine = (index) => {
        return {
            lineId: index + 1,
            revenueCode: '',
            procedureCode: '',
            modifierOne: '',
            modifierTwo: '',
            serviceDate: '',
            units: 0,
            billed: ''
        }
    };

    const getDefaultLines = () => {
        return [getNewLine(0), getNewLine(1), getNewLine(2), getNewLine(3), getNewLine(4)];
    };

    const linesCollection = useCollection(getDefaultLines(), getNewLine, 'lines');

    const prepareLines = linesCollection => prepareServiceLines(linesCollection, line => line.revenueCode);

    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        "claimId": claimId,
        "providerName": providerName,
        "medicareId": medicareId,
        "npi": npi,
        "ndc": ndc,
        "providerZipCode": providerZipCode,
        "fromDate": fromDate,
        "toDate": toDate,
        "height": height,
        "weight": weight,
        "dateOfBirth": dateOfBirth,
        "dialysisOnsetDate": dialysisOnsetDate,
        "pericarditis": pericarditis,
        "gasrolTractBleeding": gastrolTractBleeding,
        "hereditaryAnemia": hereditaryAnemia,
        "myelodysplasticSyndrome": myelodysplasticSyndrome,
        "dialysisConditionCodeOne": dialysisConditionCodeOne,
        "dialysisConditionCodeTwo": dialysisConditionCodeTwo,
        "dialysisConditionCodeThree": dialysisConditionCodeThree,
        "dialysisConditionCodeFour": dialysisConditionCodeFour,
        "dialysisConditionCodeFive": dialysisConditionCodeFive,
        "diagnosisCodeOne": diagnosisCodeOne,
        "diagnosisCodeTwo": diagnosisCodeTwo,
        "diagnosisCodeThree": diagnosisCodeThree,
        "diagnosisCodeFour": diagnosisCodeFour,
        "diagnosisCodeFive": diagnosisCodeFive,
        "diagnosisCodeSix": diagnosisCodeSix,
        "diagnosisCodeSeven": diagnosisCodeSeven,
        "diagnosisCodeEight": diagnosisCodeEight,
        "diagnosisCodeNine": diagnosisCodeNine,
        "diagnosisCodeTen": diagnosisCodeTen,
        "lines": linesCollection.values
    });

    const handleFileSave = () => {
        pricer.reset();
        fileExportExecute();
    };

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const handleFileImportOnChange = (e) => {
        pricer.reset();
        fileImportOnChange(e);
    };

    const handleSubmit = async () => {
        fileExportReset();
        fileImportReset();

        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(calendarYear, formatRequest());
        }
    };

    const formatRequest = () => {
        return {
            claimId: claimId || null,
            medicareId: medicareId || null,
            npi: npi || null,
            zipCode: providerZipCode || null,
            fromServiceDate: formatDateStringIfMatches(fromDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toServiceDate: formatDateStringIfMatches(toDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            dob: formatDateStringIfMatches(dateOfBirth, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT) || null,
            weight: weight || null,
            height: height || null,
            ndc: ndc || null,
            dialysisOnsetDate: formatDateStringIfMatches(dialysisOnsetDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            dialysisCondCode1: dialysisConditionCodeOne || null,
            dialysisCondCode2: dialysisConditionCodeTwo || null,
            dialysisCondCode3: dialysisConditionCodeThree || null,
            dialysisCondCode4: dialysisConditionCodeFour || null,
            dialysisCondCode5: dialysisConditionCodeFive || null,
            icdDiag1: diagnosisCodeOne || null,
            icdDiag2: diagnosisCodeTwo || null,
            icdDiag3: diagnosisCodeThree || null,
            icdDiag4: diagnosisCodeFour || null,
            icdDiag5: diagnosisCodeFive || null,
            icdDiag6: diagnosisCodeSix || null,
            icdDiag7: diagnosisCodeSeven || null,
            icdDiag8: diagnosisCodeEight || null,
            icdDiag9: diagnosisCodeNine || null,
            icdDiag10: diagnosisCodeTen || null,
            pericarditis: pericarditis ? 'Y' : 'N',
            gastroIntestinalTractBleeding: gastrolTractBleeding ? 'Y' : 'N',
            hereditaryHemolyticSickleCellAnemia: hereditaryAnemia ? 'Y' : 'N',
            myelodysplasticSyndrome: myelodysplasticSyndrome ? 'Y' : 'N',
            manualAdjFlag: 'No',
            serviceLineItems: prepareLines(linesCollection)
        };
    }

    const handleReset = () => {
        setClaimId('');
        setProviderName('');
        setMedicareId('');
        setNpi('');
        setProviderZipCode('');
        setFromDate(getDefaultDateString());
        setToDate(getDefaultDateString());
        setHeight('');
        setWeight('');
        setNdc('');
        setDateOfBirth('');
        setDialysisOnsetDate('');
        setPericarditis(false);
        setGastroTractBleeding(false);
        setHereditaryAnemia(false);
        setMyelodysplasticSyndrome(false);
        setDialysisConditionCodeOne('');
        setDialysisConditionCodeTwo('');
        setDialysisConditionCodeThree('');
        setDialysisConditionCodeFour('');
        setDialysisConditionCodeFive('');
        setDiagnosisCodeOne('');
        setDiagnosisCodeTwo('');
        setDiagnosisCodeThree('');
        setDiagnosisCodeFour('');
        setDiagnosisCodeFive('');
        setDiagnosisCodeSix('');
        setDiagnosisCodeSeven('');
        setDiagnosisCodeEight('');
        setDiagnosisCodeNine('');
        setDiagnosisCodeTen('');
        linesCollection.setValues(getDefaultLines());
        pricer.reset();
        fileImportReset();
        fileExportReset();
    };

    useEffect(() => {
        if (fileImportSuccess === true) {
            const setters = {
                "claimId": setClaimId,
                "providerName": setProviderName,
                "medicareId": setMedicareId,
                "npi": setNpi,
                "ndc": setNdc,
                "providerZipCode": setProviderZipCode,
                "fromDate": setFromDate,
                "toDate": setToDate,
                "height": setHeight,
                "weight": setWeight,
                "dateOfBirth": setDateOfBirth,
                "dialysisOnsetDate": setDialysisOnsetDate,
                "pericarditis": setPericarditis,
                "gasrolTractBleeding": setGastroTractBleeding,
                "hereditaryAnemia": setHereditaryAnemia,
                "myelodysplasticSyndrome": setMyelodysplasticSyndrome,
                "dialysisConditionCodeOne": setDialysisConditionCodeOne,
                "dialysisConditionCodeTwo": setDialysisConditionCodeTwo,
                "dialysisConditionCodeThree": setDialysisConditionCodeThree,
                "dialysisConditionCodeFour": setDialysisConditionCodeFour,
                "dialysisConditionCodeFive": setDialysisConditionCodeFive,
                "diagnosisCodeOne": setDiagnosisCodeOne,
                "diagnosisCodeTwo": setDiagnosisCodeTwo,
                "diagnosisCodeThree": setDiagnosisCodeThree,
                "diagnosisCodeFour": setDiagnosisCodeFour,
                "diagnosisCodeFive": setDiagnosisCodeFive,
                "diagnosisCodeSix": setDiagnosisCodeSix,
                "diagnosisCodeSeven": setDiagnosisCodeSeven,
                "diagnosisCodeEight": setDiagnosisCodeEight,
                "diagnosisCodeNine": setDiagnosisCodeNine,
                "diagnosisCodeTen": setDiagnosisCodeTen,
                "lines": linesCollection.setValues
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        setCalendarYearInputDate(fromDate);
    }, [fromDate]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    result: pricer.result,
                    calendarYear,
                    lines: prepareLines(linesCollection)
                }
            });
        }
    }, [pricer.success]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>Dialysis Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>

            {(pricer.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            <form noValidate={true}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <ProviderMapper inputs={[
                        {
                            name: "providerName",
                            label: "Provider Name",
                            superscript: "1",
                            value: providerName,
                            onChange: setProviderName
                        },
                        {
                            name: "medicareId",
                            label: "Medicare ID",
                            superscript: "1",
                            value: medicareId,
                            onChange: setMedicareId
                        },
                        {
                            name: "npi",
                            label: "NPI",
                            sublabel: "(facilities may have multiple NPIs)",
                            superscript: "1",
                            value: npi,
                            onChange: setNpi
                        },
                        {
                            name: "zipcode",
                            onChange: setProviderZipCode,
                            isSubordinate: true,
                        }
                    ]} model={modelType} />
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Provider ZIP Code" maxLength={5} labelClassName="fw-bold" id="provider-zip" onChange={setProviderZipCode} value={providerZipCode} />
                    </div>
                    {enableESRDNdc && <div className="col-md-3">
                        <Input label="NDC" labelClassName="fw-bold" id="ndc" value={ndc} onChange={setNdc} maxLength={11} />
                    </div>}
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="From Date" preservePlaceholder={true} labelClassName="fw-bold" id="from-date" type="date" onChange={setFromDate} value={fromDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3">
                        <Input label="To Date" preservePlaceholder={true} labelClassName="fw-bold" id="to-date" type="date" onChange={setToDate} value={toDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3 align-self-center mt-4">
                        {calendarYear && <>Calendar Year {calendarYear}</>}
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Height (cm)" labelClassName="fw-bold" id="height" type="number" onChange={setHeight} value={height} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Weight (kg)" labelClassName="fw-bold" id="weight" type="number" onChange={setWeight} value={weight} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Date of Birth" preservePlaceholder={true} labelClassName="fw-bold" id="date-of-birth" type="date" onChange={setDateOfBirth} value={dateOfBirth} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Dialysis Onset Date" preservePlaceholder={true} labelClassName="fw-bold" id="dialysis-onset-date" type="date" onChange={setDialysisOnsetDate} value={dialysisOnsetDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                </div>
                <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
                <div className="fw-bold">
                    Chronic & Acute Comorbidities - Manual Input
                    <br />
                    <span className="fst-italic">Conditions may also be indicated by ICD diagnosis codes.</span>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Checkbox label="Pericarditis" id="pericarditis" onChange={setPericarditis} value={pericarditis} />
                    </div>
                    <div className="col-md-3">
                        <Checkbox label="Gastro-Intestinal Tract Bleeding" id="gastro-tract-bleeding" onChange={setGastroTractBleeding} value={gastrolTractBleeding} />
                    </div>
                    <div className="col-md-3">
                        <Checkbox label="Hereditary Hemolytic or Sickle Cell Anemia" id="hereditary-anemia" onChange={setHereditaryAnemia} value={hereditaryAnemia} />
                    </div>
                    <div className="col-md-3">
                        <Checkbox label="Myelodysplastic Syndrome" id="myelodysplastic-syndrome" onChange={setMyelodysplasticSyndrome} value={myelodysplasticSyndrome} />
                    </div>
                </div>
                <div className="fw-bold">Dialysis Condition Codes</div>
                <div className="row my-2">
                    <div className="col-2">
                        <Input label="Code 1" maxLength={2} id="dialysis-code-1" onChange={setDialysisConditionCodeOne} value={dialysisConditionCodeOne} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 2" maxLength={2} id="dialysis-code-2" onChange={setDialysisConditionCodeTwo} value={dialysisConditionCodeTwo} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 3" maxLength={2} id="dialysis-code-3" onChange={setDialysisConditionCodeThree} value={dialysisConditionCodeThree} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 4" maxLength={2} id="dialysis-code-4" onChange={setDialysisConditionCodeFour} value={dialysisConditionCodeFour} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 5" maxLength={2} id="dialysis-code-5" onChange={setDialysisConditionCodeFive} value={dialysisConditionCodeFive} />
                    </div>
                </div>
                <div className="fw-bold">ICD Diagnosis Codes</div>
                <div className="fst-italic fw-bold">Enter ICD codes without the decimal.</div>
                <div className="row my-2">
                    <div className="col-2">
                        <Input label="Code 1" maxLength={7} id="diagnosis-code-1" onChange={setDiagnosisCodeOne} value={diagnosisCodeOne.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 2" maxLength={7} id="diagnosis-code-2" onChange={setDiagnosisCodeTwo} value={diagnosisCodeTwo.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 3" maxLength={7} id="diagnosis-code-3" onChange={setDiagnosisCodeThree} value={diagnosisCodeThree.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 4" maxLength={7} id="diagnosis-code-4" onChange={setDiagnosisCodeFour} value={diagnosisCodeFour.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 5" maxLength={7} id="diagnosis-code-5" onChange={setDiagnosisCodeFive} value={diagnosisCodeFive.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 6" maxLength={7} id="diagnosis-code-6" onChange={setDiagnosisCodeSix} value={diagnosisCodeSix.toUpperCase()} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-2">
                        <Input label="Code 7" maxLength={7} id="diagnosis-code-7" onChange={setDiagnosisCodeSeven} value={diagnosisCodeSeven.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 8" maxLength={7} id="diagnosis-code-8" onChange={setDiagnosisCodeEight} value={diagnosisCodeEight.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 9" maxLength={7} id="diagnosis-code-9" onChange={setDiagnosisCodeNine} value={diagnosisCodeNine.toUpperCase()} />
                    </div>
                    <div className="col-2">
                        <Input label="Code 10" maxLength={7} id="diagnosis-code-10" onChange={setDiagnosisCodeTen} value={diagnosisCodeTen.toUpperCase()} />
                    </div>
                </div>
                <hr />
                <div className="row g-0">
                    <div className="col my-2">
                        <EntryTable id="service-line-items"
                            collection={linesCollection}
                            lineCountLabel="Total Lines"
                            disableMaxWarningMessage={true}
                            max={100}
                            upDownNavigation={true}
                            runningTotal={{
                                name: 'billed',
                                className: 'me-2',
                                formatter: formatStringAsCurrency
                            }}>
                            <EntryTableColumn label="Line ID"
                                name="lineId"
                                type="label"
                                className="mw-50" />
                            <EntryTableColumn label="Revenue Code"
                                name="revenueCode"
                                type="text" />
                            <EntryTableColumn label="Procedure Code (CPT/HCPCS)"
                                name="procedureCode"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Modifier 1"
                                name="modifierOne"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Modifier 2"
                                name="modifierTwo"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Service Date"
                                name="serviceDate"
                                type="date"
                                className="mw-125"
                                placeholder={DATE_STRING_FORMAT.toUpperCase()}
                                onValidate={(value) => validateLineServiceDate(value, fromDate, toDate)} />
                            <EntryTableColumn label="Units"
                                name="units"
                                type="number"
                                className="mw-75"
                                valueTransform={transformNumberToOneDecimalPlace} />
                            <EntryTableColumn label="Billed"
                                name="billed"
                                type="text"
                                className="mw-100 text-end"
                                onValidate={validateBilled} />
                        </EntryTable>
                    </div>
                </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton className="btn-outline-secondary" id="load-request-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    loading={fileImporting}
                                    onChange={handleFileImportOnChange}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={pricer.error || fileImportError || fileExportError} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" id="reset-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleReset}
                                    text="Reset" />
                            </div>
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" id="submit-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    loading={pricer.loading}
                                    text="Submit"
                                    onClick={handleSubmit} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
